import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TableCell,
  TableRow,
  Typography,
  IconButton,
  Box,
  Chip,
  Stack,
} from "@mui/material";
import { Edit, Download, Delete } from "@mui/icons-material";
import styles from "./BookListing.module.scss";
import CustomDialog from "../../../../library/common/CustomDialog";
import { api } from "../../../../services";
import { RemoveRedEye } from "@mui/icons-material";
import { CONSTANTS } from "../../../../library/common/constant";
import { throttle } from "lodash";

const { DELETE_CONFIRM_TITLE, DELETE_CONFIRM_MESSAGE, THROTTLE_DURATION } =
  CONSTANTS;

const BookItem = ({ index, bookItem, getBookList }) => {
  const navigate = useNavigate();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenDeleteDialogue = () => {
    setDialogOpen(true);
  };

  const handleCloseDeleteDialogue = () => {
    setDialogOpen(false);
  };

  const deleteBook = throttle(async () => {
    try {
      setIsLoading(true);
      await api.delete(`/books/${bookItem?._id}`);
      await getBookList();
      handleCloseDeleteDialogue();
    } catch (error) {
      console.error("Failed to delete: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  return (
    <>
      {bookItem && (
        <TableRow className={styles.table_row}>
          <TableCell>{index}</TableCell>
          <TableCell style={{ position: "relative", maxWidth: "200px" }}>
            <Stack>
              <Typography noWrap title={bookItem?.slug}>{bookItem?.slug}</Typography>
              <Stack direction="row" gap={1}>
                {bookItem?.isFeatured && (
                  <Chip
                    size="small"
                    label="Featured"
                    sx={{
                      backgroundColor: "#ffe082",
                      color: "#b26a00",
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: 12,
                      height: 24,
                    }}
                  />
                )}
                {bookItem?.publishDate &&
                  new Date(bookItem.publishDate) > new Date() && (
                    <Chip
                      size="small"
                      label="Upcoming"
                      sx={{
                        backgroundColor: "#b2ebf2",
                        color: "#006064",
                        borderRadius: "8px",
                        fontWeight: 600,
                        fontSize: 12,
                        height: 24,
                      }}
                    />
                  )}
              </Stack>
            </Stack>
          </TableCell>
          <TableCell style={{ maxWidth: "200px" }}>
            <Typography noWrap title={bookItem.title?.en}>{bookItem.title?.en}</Typography>
          </TableCell>
          <TableCell>
            <Typography>{bookItem.languageId?.name.en}</Typography>
          </TableCell>
          <TableCell>
            <Box display="flex" gap="8" justifyContent="center">
              <IconButton component="a" href={bookItem.fileUrl} target="_blank">
                <RemoveRedEye />
              </IconButton>
              <IconButton
                aria-label="download"
                onClick={async () => {
                  try {
                    const response = await fetch(bookItem.fileUrl);
                    if (!response.ok) {
                      throw new Error("Failed to download file");
                    }
                    const blob = await response.blob();
                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = bookItem.fileUrl.split("/").pop();
                    link.click();
                    window.URL.revokeObjectURL(link.href);
                  } catch (error) {
                    console.error("Error downloading the file:", error);
                  }
                }}
                download
              >
                <Download />
              </IconButton>
            </Box>
          </TableCell>
          <TableCell className={styles.icon_button_container}>
            <div className={styles.pageButtons}>
              <IconButton
                className={styles.edit_icon}
                onClick={() => navigate(`/book/edit/${bookItem?.slug}`)}
              >
                <Edit />
              </IconButton>
              <IconButton
                className={styles.delete_icon}
                onClick={handleOpenDeleteDialogue}
              >
                <Delete />
              </IconButton>
            </div>
          </TableCell>
        </TableRow>
      )}
      <CustomDialog
        open={isDialogOpen}
        title={DELETE_CONFIRM_TITLE}
        content={DELETE_CONFIRM_MESSAGE}
        onClose={handleCloseDeleteDialogue}
        onSecondaryClick={handleCloseDeleteDialogue}
        onPrimaryClick={deleteBook}
        disablePrimary={isLoading}
      />
    </>
  );
};

export default BookItem;
